import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useParams, Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  supportedLanguages,
  defaultLanguage,
  type SupportedLanguage,
} from '@/i18n'
import { Layout } from '@/components/layout'

// Code splitting: lazy load page components
const Home = lazy(() => import('@/pages/Home').then(m => ({ default: m.Home })))
const Company = lazy(() => import('@/pages/Company').then(m => ({ default: m.Company })))
const Products = lazy(() => import('@/pages/Products').then(m => ({ default: m.Products })))
const Recipes = lazy(() => import('@/pages/Recipes').then(m => ({ default: m.Recipes })))
const Sustainability = lazy(() => import('@/pages/Sustainability').then(m => ({ default: m.Sustainability })))
const Contacts = lazy(() => import('@/pages/Contacts').then(m => ({ default: m.Contacts })))
const Login = lazy(() => import('@/pages/Login').then(m => ({ default: m.Login })))
const Privacy = lazy(() => import('@/pages/Privacy').then(m => ({ default: m.Privacy })))
const CookiePolicy = lazy(() => import('@/pages/CookiePolicy').then(m => ({ default: m.CookiePolicy })))

// Loading fallback for Suspense
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    </div>
  )
}

function LanguageWrapper() {
  const { lang } = useParams<{ lang: string }>()
  const { i18n } = useTranslation()

  useEffect(() => {
    if (lang && supportedLanguages.includes(lang as SupportedLanguage)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang)
      }
    }
  }, [lang, i18n])

  // Redirect invalid language to default
  if (!lang || !supportedLanguages.includes(lang as SupportedLanguage)) {
    return <Navigate to={`/${defaultLanguage}`} replace />
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  )
}

export function LanguageRouter() {
  return (
    <Routes>
      {/* Redirect root to default language */}
      <Route path="/" element={<Navigate to={`/${defaultLanguage}`} replace />} />

      {/* Language-prefixed routes */}
      <Route path="/:lang" element={<LanguageWrapper />}>
        {/* Home page - empty path for /it and /en */}
        <Route index element={
          <Suspense fallback={<PageLoader />}>
            <Home />
          </Suspense>
        } />
        <Route path="azienda" element={
          <Suspense fallback={<PageLoader />}>
            <Company />
          </Suspense>
        } />
        <Route path="company" element={
          <Suspense fallback={<PageLoader />}>
            <Company />
          </Suspense>
        } />
        <Route path="prodotti" element={
          <Suspense fallback={<PageLoader />}>
            <Products />
          </Suspense>
        } />
        <Route path="products" element={
          <Suspense fallback={<PageLoader />}>
            <Products />
          </Suspense>
        } />
        <Route path="ricette" element={
          <Suspense fallback={<PageLoader />}>
            <Recipes />
          </Suspense>
        } />
        <Route path="recipes" element={
          <Suspense fallback={<PageLoader />}>
            <Recipes />
          </Suspense>
        } />
        <Route path="sostenibilita" element={
          <Suspense fallback={<PageLoader />}>
            <Sustainability />
          </Suspense>
        } />
        <Route path="sustainability" element={
          <Suspense fallback={<PageLoader />}>
            <Sustainability />
          </Suspense>
        } />
        <Route path="contatti" element={
          <Suspense fallback={<PageLoader />}>
            <Contacts />
          </Suspense>
        } />
        <Route path="contacts" element={
          <Suspense fallback={<PageLoader />}>
            <Contacts />
          </Suspense>
        } />
        <Route path="accedi" element={
          <Suspense fallback={<PageLoader />}>
            <Login />
          </Suspense>
        } />
        <Route path="login" element={
          <Suspense fallback={<PageLoader />}>
            <Login />
          </Suspense>
        } />
        <Route path="privacy" element={
          <Suspense fallback={<PageLoader />}>
            <Privacy />
          </Suspense>
        } />
        <Route path="cookie-policy" element={
          <Suspense fallback={<PageLoader />}>
            <CookiePolicy />
          </Suspense>
        } />
      </Route>
    </Routes>
  )
}
